import React, { useState } from 'react';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Column from '@amzn/meridian/column';
import { escapeRegExp } from 'lodash';

export interface Option {
  value: string;
  label: string;
}

interface BaseSelectorProps<T extends Option> {
  label?: string;
  placeholder?: string;
  options: T[];
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  width?: number | string;
  searchable?: boolean;
}

const BaseSelector = <T extends Option>({
  label,
  placeholder = 'Select an option...',
  options,
  value,
  onChange,
  disabled = false,
  width = 300,
  searchable = true,
}: BaseSelectorProps<T>): JSX.Element => {
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Filter options based on search query
  const filteredOptions =
    searchable && searchQuery
      ? options.filter((option) => {
          const searchRegex = new RegExp(escapeRegExp(searchQuery), 'i');
          return searchRegex.test(option.label) || searchRegex.test(option.value);
        })
      : options;

  return (
    <Select
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      searchQuery={searchable ? searchQuery : undefined}
      onSearch={searchable ? setSearchQuery : undefined}
      width={width}
      disabled={disabled}
    >
      {filteredOptions.map((option) => (
        <SelectOption key={option.value} value={option.value} label={option.label} />
      ))}
      {filteredOptions.length === 0 && searchQuery && (
        <Column alignmentVertical="center" spacing="300" spacingInset="600">
          <Text alignment="center">No results found</Text>
        </Column>
      )}
    </Select>
  );
};

export default BaseSelector;
